import { useEffect, useState } from 'react';
import axios from 'axios';
import { Save, Loader2, BookOpen, MessageSquare, Sparkles, HelpCircle, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';

/**
 * Admin → AI Coach configuration.
 * Lets the admin pick a tone preset, write custom instructions, and pin
 * studio-specific knowledge that is appended to the DJ Coach system prompt
 * on every chat turn.
 */
export default function AdminCoachPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tone, setTone] = useState('mentor');
  const [presets, setPresets] = useState({});
  const [customInstructions, setCustomInstructions] = useState('');
  const [knowledgeBase, setKnowledgeBase] = useState('');
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/coach-profile`);
        setTone(data.tone || 'mentor');
        setPresets(data.tone_presets || {});
        setCustomInstructions(data.custom_instructions || '');
        setKnowledgeBase(data.knowledge_base || '');
        setFaqs(data.faqs || []);
      } catch {
        toast.error('Failed to load Coach Profile');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/coach-profile`, {
        tone,
        custom_instructions: customInstructions,
        knowledge_base: knowledgeBase,
        faqs: faqs.filter(f => f.question.trim() && f.answer.trim()),
      });
      toast.success('Coach Profile saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addFaq = () => {
    if (faqs.length >= 50) {
      toast.error('Maximum 50 FAQ entries');
      return;
    }
    setFaqs((f) => [...f, { question: '', answer: '' }]);
  };
  const updateFaq = (idx, field, val) => {
    setFaqs((f) => f.map((item, i) => i === idx ? { ...item, [field]: val } : item));
  };
  const removeFaq = (idx) => setFaqs((f) => f.filter((_, i) => i !== idx));

  if (loading) {
    return (
      <div className="glass-pane p-12 text-center" data-testid="admin-coach-loading">
        <Loader2 className="w-6 h-6 text-[#2563FF] mx-auto animate-spin" />
      </div>
    );
  }

  const presetKeys = Object.keys(presets);

  return (
    <div className="space-y-5" data-testid="admin-coach-panel">
      {/* Header */}
      <div className="glass-pane p-5 flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl gradient-primary flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-base font-semibold text-[#0F172A]">AI Coach Configuration</h3>
          <p className="text-xs text-[#64748B] mt-1">
            This is the coach's <strong>specification sheet</strong>. Everything
            below is loaded into the system prompt on <em>every</em> chat turn
            — the bot can't forget or drift away from it. Changes apply to all
            users on the next message they send.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary flex items-center gap-2 text-sm disabled:opacity-60"
          data-testid="admin-coach-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </div>

      {/* Tone */}
      <div className="glass-pane p-5">
        <p className="font-mono text-[10px] tracking-[0.18em] text-[#2563FF] mb-2">TONE PRESET</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          {presetKeys.map((key) => {
            const active = tone === key;
            return (
              <button
                key={key}
                onClick={() => setTone(key)}
                data-testid={`admin-coach-tone-${key}`}
                className={`text-left p-3.5 rounded-xl border transition-all ${
                  active
                    ? 'border-[#2563FF] bg-[#EFF6FF] shadow-[0_4px_14px_rgba(37,99,255,0.18)]'
                    : 'border-[#E2E8F0] bg-white hover:border-[#2563FF]/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full ${active ? 'bg-[#2563FF]' : 'bg-[#CBD5E1]'}`} />
                  <p className={`text-sm font-semibold capitalize ${active ? 'text-[#2563FF]' : 'text-[#0F172A]'}`}>{key}</p>
                </div>
                <p className="text-xs text-[#64748B] leading-snug">{presets[key]}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom instructions */}
      <div className="glass-pane p-5">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="w-4 h-4 text-[#2563FF]" />
          <p className="font-mono text-[10px] tracking-[0.18em] text-[#2563FF]">CUSTOM INSTRUCTIONS</p>
        </div>
        <p className="text-xs text-[#64748B] mb-3">
          Extra rules layered on top of the base coach prompt. e.g. "Always suggest
          two alternative tracks", "Never recommend tracks above 138 BPM for warm-ups".
        </p>
        <textarea
          value={customInstructions}
          onChange={(e) => setCustomInstructions(e.target.value)}
          maxLength={4000}
          placeholder="One rule per line…"
          className="w-full min-h-[140px] p-3 rounded-xl border border-[#E2E8F0] bg-white text-sm placeholder:text-[#94A3B8] focus:border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/15 outline-none font-mono"
          data-testid="admin-coach-instructions"
        />
        <p className="text-[10px] font-mono text-[#94A3B8] mt-1 text-right">{customInstructions.length}/4000</p>
      </div>

      {/* Knowledge base */}
      <div className="glass-pane p-5">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen className="w-4 h-4 text-[#2563FF]" />
          <p className="font-mono text-[10px] tracking-[0.18em] text-[#2563FF]">KNOWLEDGE BASE</p>
        </div>
        <p className="text-xs text-[#64748B] mb-3">
          Studio-specific facts the coach should always know — your residencies,
          signature labels, transition tricks, club preferences, BPM houserules.
        </p>
        <textarea
          value={knowledgeBase}
          onChange={(e) => setKnowledgeBase(e.target.value)}
          maxLength={8000}
          placeholder="Free-form notes / style guide. Markdown not required."
          className="w-full min-h-[200px] p-3 rounded-xl border border-[#E2E8F0] bg-white text-sm placeholder:text-[#94A3B8] focus:border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/15 outline-none font-mono"
          data-testid="admin-coach-kb"
        />
        <p className="text-[10px] font-mono text-[#94A3B8] mt-1 text-right">{knowledgeBase.length}/8000</p>
      </div>

      {/* FAQs */}
      <div className="glass-pane p-5" data-testid="admin-coach-faqs">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-[#2563FF]" />
            <p className="font-mono text-[10px] tracking-[0.18em] text-[#2563FF]">FAQ KNOWLEDGE BASE</p>
            <span className="text-[10px] font-mono text-[#94A3B8]">({faqs.length}/50)</span>
          </div>
          <button
            onClick={addFaq}
            data-testid="admin-coach-faq-add"
            className="px-2.5 py-1 rounded-lg border border-[#E2E8F0] hover:border-[#2563FF]/40 text-[#2563FF] text-xs font-semibold flex items-center gap-1.5"
          >
            <Plus className="w-3 h-3" /> Add FAQ
          </button>
        </div>
        <p className="text-xs text-[#64748B] mb-4">
          Structured Q&A the coach uses verbatim when users ask matching questions.
          Great for "How do I sync?", "What's a Camelot key?", studio house rules, etc.
        </p>
        {faqs.length === 0 ? (
          <div className="text-center py-6 text-xs text-[#94A3B8] italic">
            No FAQs yet — click "Add FAQ" to seed the knowledge base.
          </div>
        ) : (
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                data-testid={`admin-coach-faq-${idx}`}
                className="p-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] space-y-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[10px] tracking-wider text-[#94A3B8]">FAQ #{idx + 1}</span>
                  <button
                    onClick={() => removeFaq(idx)}
                    data-testid={`admin-coach-faq-remove-${idx}`}
                    className="w-7 h-7 rounded-md text-[#94A3B8] hover:bg-red-50 hover:text-red-500 flex items-center justify-center"
                    title="Remove FAQ"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <input
                  value={faq.question}
                  onChange={(e) => updateFaq(idx, 'question', e.target.value)}
                  maxLength={300}
                  placeholder="Question (e.g. How do I sync my library?)"
                  data-testid={`admin-coach-faq-q-${idx}`}
                  className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white text-sm outline-none focus:border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/15"
                />
                <textarea
                  value={faq.answer}
                  onChange={(e) => updateFaq(idx, 'answer', e.target.value)}
                  maxLength={2000}
                  rows={3}
                  placeholder="Answer (concise, action-oriented)"
                  data-testid={`admin-coach-faq-a-${idx}`}
                  className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white text-sm outline-none focus:border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/15 font-mono"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
